import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Chip, IconButton, Tooltip, Box } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useTablePagination, useAppMeta } from "hooks";
import { useLazyGetShipmentsByBranchQuery } from "store/services/reportApi";

const BranchShipmentsReport = () => {
    useAppMeta("Branch Shipments");

    const { branchId } = useParams();
    const [searchParams] = useSearchParams();
    const formatDuration = (ms) => {
        if (ms === null || ms === undefined) return "N/A";
        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / (60 * 60 * 24));
        const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    const startDate = searchParams.get("startDate") || "";
    const endDate = searchParams.get("endDate") || "";
    const keyword = searchParams.get("search") || "";

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

    const [fetchShipments, { data, isLoading, isFetching, error }] =
        useLazyGetShipmentsByBranchQuery();

    React.useEffect(() => {
        if (!branchId) return;
        fetchShipments({
            branchId,
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        });
    }, [branchId, currentPage, perPage, keyword, startDate, endDate, fetchShipments]);

    const columns = [
        {
            name: "Shipment ID",
            selector: (row) => row.shipmentId,
            cell: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {row.shipmentId || "N/A"}
                    <Tooltip title="View shipment detail in a new tab">
                        <IconButton
                            size="small"
                            onClick={() => window.open(`/app/crm/shipment/${row._id}/detail`, "_blank")}
                        >
                            <OpenInNew fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            sortable: true,
        },
        {
            name: "Source Branch",
            selector: (row) => row.sourceBranch?.name,
            cell: (row) =>
                row.sourceBranch ? (
                    <Chip
                        label={`${row.sourceBranch.name} (${row.sourceBranch.code})`}
                        size="small"
                        variant="outlined"
                    />
                ) : "N/A",
        },
        {
            name: "Destination Branch",
            selector: (row) => row.destinationBranch?.name,
            cell: (row) =>
                row.destinationBranch ? (
                    <Chip
                        label={`${row.destinationBranch.name} (${row.destinationBranch.code})`}
                        size="small"
                        variant="outlined"
                        color="primary"
                    />
                ) : "N/A",
        },
        {
            name: "Delivery Time",
            selector: (row) => row.deliveryTime,
            cell: (row) => formatDuration(row.deliveryTime),
            sortable: true,
        },
    ];

    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Shipment Delivery Time", path: "/app/crm/reports/average-delivery-time" },
        { title: data?.branch ? `${data.branch.name} (${data.branch.code})` : "Branch Shipments" },
    ];

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <TableFilter
                    searchLabel="Search by shipment ID"
                    disabled={isLoading || isFetching}
                />
                <AppTable
                    columns={columns}
                    data={data?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={data?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchShipmentsReport;